import { useEffect, useRef, useState } from "react";

import { useMediaQuery, useTheme } from "@mui/material";

export const isWideDesktop = `@media only screen and (min-width: 2260px)`;
export const isDesktop = `@media only screen and (min-width: 1810px)`;
export const isSmallDesktop = `@media only screen and (min-width: 1024px)`;
// Header swap boundary (SecondRootLayout switches MobileHeader→SecondHeader here).
// The desktop header is position:fixed (needs top clearance); the mobile header is
// position:sticky (in flow). Anything reserving the fixed-header's height must key
// off THIS, not isSmallDesktop, or 1024–1149 double-reserves and pushes content down.
export const isHeaderDesktop = `@media only screen and (min-width: 1150px)`;
// Tablet-and-up boundary (phone↔tablet split) — matches the MobileHeader 600 split.
export const isTabletUp = `@media only screen and (min-width: 600px)`;
export const isSmallMobile = `@media only screen and (max-width: 320px)`;
export const isMobile = `@media only screen and (max-width: 1024px)`;

export const useIsDesktop = (): boolean => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up("md"));
};

export const useIsBigDesktop = (): boolean => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up("lg"));
};

export const useWindowSize = (element?: HTMLElement) => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    const observable = element ? element : window;

    observable.addEventListener("resize", handleResize);

    return () => observable.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};

export const useElementSize = <T extends HTMLElement>(element: T | null) => {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!element) return;

    const updateSize = () => {
      const { width, height } = element.getBoundingClientRect();
      setSize({ width, height });
    };

    updateSize();

    const observer = new ResizeObserver(() => {
      updateSize();
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [element]);

  return size;
};

export const useTitleDots = () => {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleContainerRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();
  const { width: titleW } = useElementSize(titleRef.current);
  const [areDotsHidden, setAreDotsHidden] = useState(true);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (titleContainerRef.current && titleRef.current) {
      const { width: containerWidth } = titleContainerRef.current.getBoundingClientRect();
      const { width: titleWidth } = titleRef.current.getBoundingClientRect();
      const { width: dotsWidth } = dotsRef.current.getBoundingClientRect();

      const areHidden = isDesktop ? titleWidth < containerWidth : titleWidth < containerWidth - dotsWidth;
      setAreDotsHidden(areHidden);
    }
  }, [width, titleW]);

  return { titleRef, titleContainerRef, dotsRef, areDotsHidden };
};
